/**
 * LanguageRegionModal — Blocking first-login modal that asks users
 * to explicitly choose their language and region.
 *
 * Shown once per user, before any other onboarding modal.
 * Ensures the AI responds in the correct language and product
 * recommendations match the user's local market.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeLanguage } from '../../i18n';
import type { SupportedLanguage } from '../../i18n';
import {
  SUPPORTED_REGIONS,
  REGION_CONFIG,
} from '../../config/affiliateConfig';
import type { SupportedRegion } from '../../config/affiliateConfig';

/** Display labels for the language selector — always shown in the native language. */
const LANGUAGE_LABELS: Record<string, string> = {
  en: '🇺🇸 English',
  ko: '🇰🇷 한국어',
  'zh-CN': '🇨🇳 中文',
  'pt-BR': '🇧🇷 Português',
  es: '🇪🇸 Español',
  de: '🇩🇪 Deutsch',
  fr: '🇫🇷 Français',
  it: '🇮🇹 Italiano',
  nl: '🇳🇱 Nederlands',
  sv: '🇸🇪 Svenska',
  pl: '🇵🇱 Polski',
};

interface LanguageRegionModalProps {
  /** Current i18n language code */
  currentLanguage: string;
  /** Best-guess region from timezone / browser locale */
  detectedRegion: SupportedRegion;
  /** Called when the user confirms both selections */
  onConfirm: (language: SupportedLanguage, region: SupportedRegion) => void;
  /** If true, skip the language step and go straight to region selection */
  skipLanguageStep?: boolean;
}

export default function LanguageRegionModal({
  currentLanguage,
  detectedRegion,
  onConfirm,
  skipLanguageStep = false,
}: LanguageRegionModalProps) {
  const { t } = useTranslation('modals');
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [selectedLang, setSelectedLang] = useState<SupportedLanguage>(
    SUPPORTED_LANGUAGES.includes(currentLanguage as SupportedLanguage)
      ? (currentLanguage as SupportedLanguage)
      : 'en'
  );
  const [selectedRegion, setSelectedRegion] = useState<SupportedRegion>(detectedRegion);
  const [step, setStep] = useState<1 | 2>(skipLanguageStep ? 2 : 1);

  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const handleLangSelect = useCallback((lang: SupportedLanguage) => {
    setSelectedLang(lang);
    // Live-switch the UI so the user sees immediate feedback
    changeLanguage(lang).catch(() => { /* non-critical preview */ });
  }, []);

  const handleNext = useCallback(() => {
    setStep(2);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(selectedLang, selectedRegion);
  }, [selectedLang, selectedRegion, onConfirm]);

  return (
    <div className="modal-overlay" data-testid="language-region-modal">
      <div
        className="modal-content language-region-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lr-title"
        aria-describedby="lr-desc"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Step 1: Language */}
        {step === 1 && (
          <>
            <div className="lr-header">
              <span className="lr-icon" aria-hidden="true">🌐</span>
              <h3 id="lr-title" ref={headingRef} tabIndex={-1}>
                {t('langRegion.langTitle', 'Choose your language')}
              </h3>
              <p id="lr-desc" className="lr-subtitle">
                {t(
                  'langRegion.langDesc',
                  'Our AI assistant will respond in your chosen language and show products available in your region.'
                )}
              </p>
            </div>

            <div className="lr-grid" aria-label={t('langRegion.langTitle', 'Choose your language')}>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  aria-pressed={selectedLang === lang}
                  className={`lr-option${selectedLang === lang ? ' lr-option--selected' : ''}`}
                  onClick={() => handleLangSelect(lang)}
                >
                  {LANGUAGE_LABELS[lang] ?? lang}
                </button>
              ))}
            </div>

            <div className="lr-actions">
              <button
                className="modal-btn modal-btn--primary"
                onClick={handleNext}
                data-testid="lr-next"
              >
                {t('langRegion.next', 'Next →')}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Region */}
        {step === 2 && (
          <>
            <div className="lr-header">
              <span className="lr-icon" aria-hidden="true">📍</span>
              <h3 id="lr-title" ref={headingRef} tabIndex={-1}>
                {t('langRegion.regionTitle', 'Select your region')}
              </h3>
              <p id="lr-desc" className="lr-subtitle">
                {t(
                  'langRegion.regionDesc',
                  'This helps us show the right prices, currency, and product availability for your area.'
                )}
              </p>
            </div>

            <div className="lr-grid" aria-label={t('langRegion.regionTitle', 'Select your region')}>
              {SUPPORTED_REGIONS.map((region) => {
                const rc = REGION_CONFIG[region];
                const label = selectedLang === 'ko' ? rc.labelKo : rc.label;
                return (
                  <button
                    key={region}
                    aria-pressed={selectedRegion === region}
                    className={`lr-option${selectedRegion === region ? ' lr-option--selected' : ''}`}
                    onClick={() => setSelectedRegion(region)}
                  >
                    {rc.flag} {label}
                  </button>
                );
              })}
            </div>

            <div className="lr-actions lr-actions--split">
              <button
                className="modal-btn modal-btn--secondary"
                onClick={() => setStep(1)}
                data-testid="lr-back"
              >
                {t('langRegion.back', '← Back')}
              </button>
              <button
                className="modal-btn modal-btn--primary"
                onClick={handleConfirm}
                data-testid="lr-confirm"
              >
                {t('langRegion.confirm', "Let's go!")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
